// src/components/features/RewriteDescriptionForm.js

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { logEvent } from 'firebase/analytics';
import { doc, updateDoc } from 'firebase/firestore';
import {
  Loader2, Sparkles, Save, Edit3, PackageCheck, XCircle, PlusCircle, Link2,
  RefreshCw, Eye, Code2, Copy, Download, CheckCircle, Edit, Wand2, Lightbulb,
  Gift, Video, Search, MousePointerClick, X
} from 'lucide-react';

import { useStandards } from '../../hooks/useStandards';
import SectionTitle from '../ui/SectionTitle';
import { analytics, functionsInstance, db } from '../../firebase/firebase';
import { extractProductNameFromUrl } from '../../utils/helpers';
import { DESCRIPTION_TONES, DESCRIPTION_LENGTHS_REWRITE, UNIQUE_SORTED_RESOURCE_COMPONENTS, GRADE_LEVELS_CONFIG, SUBJECT_CONFIG, PEDAGOGICAL_APPROACHES } from '../../config/constants';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import { STANDARDS_BY_SUBJECT } from '../../config/standards';

const SEASONAL_THEMES = [
  "Spring", "Summer", "Fall / Autumn", "Winter",
  "Back to School", "End of School Year", "Mid-Term / Test Prep",
  "New Year's", "Martin Luther King Jr. Day", "Valentine's Day",
  "Presidents' Day", "St. Patrick's Day", "Easter",
  "Earth Day", "Mother's Day", "Memorial Day", "Father's Day",
  "Independence Day (4th of July)", "Labor Day", "Halloween",
  "Veterans Day", "Thanksgiving", "Christmas", "Hanukkah", "Kwanzaa",
  "100th Day of School", "Dr. Seuss Day (Read Across America)", "Cinco de Mayo"
];

// --- SearchableDropdown Component (Instant Search, No Debounce, Unique Keys) ---
const SearchableDropdown = React.memo(({ options, value, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const dropdownRef = useRef(null);

  const filteredOptions = useMemo(() => {
    if (!inputValue.trim()) return options;
    const search = inputValue.toLowerCase();
    return options.filter((option) =>
      option.label.toLowerCase().includes(search)
    );
  }, [options, inputValue]);

  const selectedLabel = useMemo(() => {
    const selectedOption = options.find((option) => option.value === value);
    return selectedOption ? selectedOption.label : (placeholder || 'Select an option');
  }, [options, value, placeholder]);

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Reset search when the dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setInputValue('');
    }
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        className="w-full p-3 border border-slate-300 rounded-lg shadow-sm bg-white text-left focus:outline-none focus:ring-2 focus:ring-sky-500 flex justify-between items-center"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className="truncate">{selectedLabel}</span>
        <span className="ml-2">▼</span>
      </button>

      {isOpen && (
        <div className="absolute z-20 w-full bg-white border border-slate-200 rounded-lg shadow-lg mt-1 max-h-60 overflow-y-auto">
          <div className="p-2 border-b border-slate-200 sticky top-0 bg-white">
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
              <input
                type="text"
                placeholder="Search..."
                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-sky-500 text-sm"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                autoFocus
              />
            </div>
          </div>
          {filteredOptions.length === 0 ? (
            <div className="p-3 text-sm text-slate-500">No results found.</div>
          ) : (
            <ul>
              {filteredOptions.slice(0, 200).map((option, index) => (
                <li
                  key={`${option.value}-${index}`}
                  className="px-3 py-2 text-sm hover:bg-sky-50 cursor-pointer"
                  onClick={() => handleSelect(option.value)}
                >
                  {option.label}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
});

// --- MultiSelectDropdown Component (Instant Search, No Debounce, Unique Keys) ---
const MultiSelectDropdown = React.memo(({
  options,
  selectedValues,
  onChange,
  buttonLabelPrefix,
  placeholder,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const dropdownRef = useRef(null);

  const filteredOptions = useMemo(() => {
    if (!inputValue.trim()) return options;
    const search = inputValue.toLowerCase();
    return options.filter((option) =>
      option.label.toLowerCase().includes(search)
    );
  }, [options, inputValue]);

  const displayLabel = useMemo(() => {
    if (!selectedValues || selectedValues.length === 0) {
      return placeholder || (buttonLabelPrefix ? `Select ${buttonLabelPrefix}...` : 'Select options...');
    }
    if (buttonLabelPrefix === 'Products' || selectedValues.length > 2) {
      return `${selectedValues.length} selected`;
    }
    const selectedLabels = options
      .filter((option) => selectedValues.includes(option.value))
      .map((option) => option.label);
    return selectedLabels.join(', ');
  }, [selectedValues, options, buttonLabelPrefix, placeholder]);

  const toggleOption = (e, optionValue) => {
    e.preventDefault();
    e.stopPropagation();

    if (!Array.isArray(selectedValues)) {
      onChange([optionValue]);
      return;
    }
    if (selectedValues.includes(optionValue)) {
      onChange(selectedValues.filter((v) => v !== optionValue));
    } else {
      onChange([...selectedValues, optionValue]);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setInputValue('');
    }
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        className="w-full p-3 border border-slate-300 rounded-lg shadow-sm bg-white text-left focus:outline-none focus:ring-2 focus:ring-sky-500 flex justify-between items-center"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className="truncate pr-2">{displayLabel}</span>
        <span className="ml-2 flex-shrink-0">▼</span>
      </button>

      {isOpen && (
        <div className="absolute z-20 w-full bg-white border border-slate-200 rounded-lg shadow-lg mt-1 max-h-60 overflow-y-auto">
          <div className="p-2 border-b border-slate-200 sticky top-0 bg-white">
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
              <input
                type="text"
                placeholder="Search..."
                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-sky-500 text-sm"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                autoFocus
              />
            </div>
          </div>
          {filteredOptions.length === 0 ? (
            <div className="p-3 text-sm text-slate-500">No results found.</div>
          ) : (
            <ul>
              {filteredOptions.slice(0, 200).map((option, index) => {
                const isSelected = selectedValues?.includes(option.value);
                return (
                  <li
                    key={`${option.value}-${index}`}
                    className={`px-3 py-2 text-sm flex items-center justify-between cursor-pointer hover:bg-sky-50 ${
                      isSelected ? 'bg-sky-50' : ''
                    }`}
                    onMouseDown={(e) => toggleOption(e, option.value)}
                  >
                    <span className="truncate">{option.label}</span>
                    {isSelected && (
                      <CheckCircle size={14} className="text-sky-500 ml-2 flex-shrink-0" />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
});

// --- DomainCheckboxGroup ---
const DomainCheckboxGroup = ({ title, options, selectedValues, onChange }) => {
  return (
    <div>
      <h4 className="font-semibold text-sm text-slate-700 mb-2">{title}</h4>
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {options.map((domain) => (
          <label key={domain} className="flex items-center space-x-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              className="form-checkbox"
              checked={selectedValues.includes(domain)}
              onChange={() => onChange(domain)}
            />
            <span>{domain}</span>
          </label>
        ))}
      </div>
    </div>
  );
};


// --- Enhanced StandardsSelectionSection ---
const StandardsSelectionSection = ({ standards, selectedIds, onSelectionChange, primarySubject, gradeLevels, selectedDomains }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredAndSearchedStandards = useMemo(() => {
    if (!searchTerm) {
      return standards;
    }
    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    return standards.filter(s =>
      s.code.toLowerCase().includes(lowerCaseSearchTerm) ||
      s.description.toLowerCase().includes(lowerCaseSearchTerm)
    );
  }, [standards, searchTerm]);

  const hasSelections = selectedIds.length > 0;

  if (standards.length === 0) {
    if (primarySubject && gradeLevels.length > 0 && selectedDomains.length > 0) {
      return <p className="text-slate-500 italic">No standards found for the selected subject, grades, and domains.</p>;
    }
    return <p className="text-slate-500 italic">Select a subject, grade level(s), and domain(s) to view standards.</p>;
  }

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          placeholder="Search standards by code or description..."
          className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sky-500 text-sm"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      <div className="max-h-80 overflow-y-auto border border-slate-200 rounded-lg p-2 bg-white">
        {filteredAndSearchedStandards.length === 0 ? (
          <p className="p-2 text-sm text-slate-500 italic">No standards match your search query.</p>
        ) : (
          <ul className="space-y-1">
            {filteredAndSearchedStandards.map(standard => (
              <li key={standard.id} className="flex items-start text-sm p-1 hover:bg-slate-50 rounded-md">
                <input
                  type="checkbox"
                  className="form-checkbox mt-1 mr-2"
                  checked={selectedIds.includes(standard.id)}
                  onChange={() => onSelectionChange(standard.id)}
                />
                <div>
                  <strong className="text-slate-700">{standard.code}:</strong> {standard.description}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      {hasSelections && (
        <div className="mt-2 text-right text-xs text-slate-600">
          Selected: {selectedIds.length} standard(s)
        </div>
      )}
    </div>
  );
};


// subject-agnostic grade key resolver
const resolveStandardGradeKeys = (subject, uiGrade) => {
  const subjectStandards = STANDARDS_BY_SUBJECT[subject];
  if (!subjectStandards) return [];

  const hasKey = (k) => Object.prototype.hasOwnProperty.call(subjectStandards, k);
  const coalesce = (band) => {
    const variants = [band, band.replace('–', '-'), band.replace('-', '–')];
    for (const v of variants) if (hasKey(v)) return v;
    return null;
  };

  if (hasKey(uiGrade)) return [uiGrade];

  const keys = Object.keys(subjectStandards);
  const hasHSSplit = keys.includes('High School (9-10)') || keys.includes('High School (11-12)');
  const hasBands = ['K-2','K–2','3-5','3–5','6-8','6–8','K-12','K–12'].some(hasKey);

  const uiIsHS = [
    'High School', 'High School (General 9-12)',
    '9th Grade', '10th Grade', '11th Grade', '12th Grade'
  ].includes(uiGrade);

  if (uiIsHS) {
    if (hasHSSplit) {
      if (['9th Grade', '10th Grade'].includes(uiGrade)) {
        return keys.includes('High School (9-10)') ? ['High School (9-10)'] : [];
      }
      if (['11th Grade', '12th Grade'].includes(uiGrade)) {
        return keys.includes('High School (11-12)') ? ['High School (11-12)'] : [];
      }
      return ['High School (9-10)', 'High School (11-12)'].filter(k => keys.includes(k));
    }
    if (keys.includes('High School')) return ['High School'];
  }

  if (hasBands) {
    const k2  = coalesce('K-2');
    const g35 = coalesce('3-5');
    const g68 = coalesce('6-8');

    if (['Pre-K','Kindergarten','1st Grade','2nd Grade'].includes(uiGrade) && k2) return [k2];
    if (['3rd Grade','4th Grade','5th Grade'].includes(uiGrade) && g35) return [g35];
    if (['6th Grade','7th Grade','8th Grade'].includes(uiGrade) && g68) return [g68];
  }

  if (uiGrade === 'Functional Skills (All Levels)' && hasKey('Functional Skills (All Levels)')) {
    return ['Functional Skills (All Levels)'];
  }
  if (['Not Grade Specific','All Grades'].includes(uiGrade)) {
    if (hasKey('Not Grade Specific')) return ['Not Grade Specific'];
    const k12 = coalesce('K-12');
    if (k12) return [k12];
  }
  return keys;
};

/**
 * Prompt to refine an existing description
 */
const buildRefinePrompt = ({ currentTitle, currentDescription, rephraseRequest }) => {
  const systemPrompt = `You are an expert TPT copywriter. Your task is to edit and refine an existing HTML description based on a user's request.
You MUST maintain the exact HTML structure (p, h3, ul, li, strong, a).
Only change the text content to meet the user's request.
Return *only* the new, full HTML description.`;

  const userPrompt = `
**USER REQUEST:**
"${rephraseRequest}"

**CURRENT PRODUCT TITLE:**
"${currentTitle}"

**CURRENT HTML DESCRIPTION (to refine):**
${currentDescription}

**YOUR TASK:**
Rewrite the full HTML description above to incorporate the user's request.
Remember to maintain the HTML structure and do not add any commentary.
`.trim();

  return { systemPrompt, userPrompt };
};


const RewriteDescriptionForm = ({
  currentUser,
  userTptProducts,
  selectedTptProductForRewrite,
  triggerAuthModal,
  onSaveComplete
}) => {
  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'bullet' }, { 'list': 'ordered'}],
      ['link']
    ],
  };

  const [rewriteProductTitle, setRewriteProductTitle] = useState('');
  const [existingDescriptionInput, setExistingDescriptionInput] = useState('');
  const [rewrittenDescriptionOutput, setRewrittenDescriptionOutput] = useState('');
  const [rewrittenNewTitle, setRewrittenNewTitle] = useState('');
  const [seoInsights, setSeoInsights] = useState(null);
  const [rewriteTone, setRewriteTone] = useState(DESCRIPTION_TONES[0]);
  const [rewriteDescriptionLength, setRewriteDescriptionLength] = useState(DESCRIPTION_LENGTHS_REWRITE[1].value);
  const [rewriteSpecifics, setRewriteSpecifics] = useState('');
  const [rewritePrimarySubject, setRewritePrimarySubject] = useState('');
  const [rewriteGradeLevels, setRewriteGradeLevels] = useState([]);
  const [rewriteResourceComponents, setRewriteResourceComponents] = useState([]);
  const [rewritePedagogicalApproaches, setRewritePedagogicalApproaches] = useState([]);
  const [rewriteSeasonalTheme, setRewriteSeasonalTheme] = useState('');
  const [rewriteRelatedProducts, setRewriteRelatedProducts] = useState([{ id: Date.now(), url: '' }]);
  const [rewriteSuggestNewTitle, setRewriteSuggestNewTitle] = useState(false);
  const [rewriteSuggestLearningOutcomes, setRewriteSuggestLearningOutcomes] = useState(true);
  const [rewriteIncludeRelatedProducts, setRewriteIncludeRelatedProducts] = useState(true);
  const [rewriteIncludeEmojis, setRewriteIncludeEmojis] = useState(false);
  const [rewriteWantStandards, setRewriteWantStandards] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [rewriteSelectedDomains, setRewriteSelectedDomains] = useState([]);
  const [rewriteSelectedStandardCodes, setRewriteSelectedStandardCodes] = useState([]);
  const [isRewriting, setIsRewriting] = useState(false);
  const [isSavingRewrite, setIsSavingRewrite] = useState(false);
  const [isSuggestingTitle, setIsSuggestingTitle] = useState(false);
  const [isFetchingInsights, setIsFetchingInsights] = useState(false);
  const [rewriteError, setRewriteError] = useState('');
  const [copySuccess, setCopySuccess] = useState('');
  const [inputViewMode, setInputViewMode] = useState('edit');
  const [outputViewMode, setOutputViewMode] = useState('preview');

  // NEW: State for the refine feature
  const [refineRequest, setRefineRequest] = useState('');

  // --- NEW: State for interactive modal ---
  const [showInteractiveModal, setShowInteractiveModal] = useState(false);

  // --- NEW: State for Saving Manual Edits ---
  const [isSavingManual, setIsSavingManual] = useState(false);

  // --- NEW: State for Suggest More Titles panel ---
  const [isSuggestingMoreTitles, setIsSuggestingMoreTitles] = useState(false);
  const [moreTitleSuggestions, setMoreTitleSuggestions] = useState([]);
  const [showMoreTitlesPanel, setShowMoreTitlesPanel] = useState(false);
  const [copiedMoreTitle, setCopiedMoreTitle] = useState(null);
  const [savingMoreTitle, setSavingMoreTitle] = useState(null); // tracks which title row is saving

  const { filteredStandards, isLoading: standardsAreLoading, error: standardsError } = useStandards(
    rewritePrimarySubject,
    rewriteGradeLevels,
    rewriteSelectedDomains
  );

  const subjectHasStandards = useMemo(() => {
    const map = STANDARDS_BY_SUBJECT[rewritePrimarySubject];
    return !!(map && Object.keys(map).length);
  }, [rewritePrimarySubject]);

  const availableDomains = useMemo(() => {
    if (!rewritePrimarySubject || rewriteGradeLevels.length === 0) return [];
    const subjectStandards = STANDARDS_BY_SUBJECT[rewritePrimarySubject];
    if (!subjectStandards) return [];
    const domains = new Set();
    for (const g of rewriteGradeLevels) {
      const gradeKeys = resolveStandardGradeKeys(rewritePrimarySubject, g);
      for (const key of gradeKeys) {
        const block = subjectStandards[key];
        if (!block) continue;
        Object.keys(block).forEach(d => domains.add(d));
      }
    }
    return Array.from(domains);
  }, [rewritePrimarySubject, rewriteGradeLevels]);

  useEffect(() => {
    setRewriteSelectedDomains(prev => prev.filter(d => availableDomains.includes(d)));
  }, [availableDomains]);

  useEffect(() => {
    if (!subjectHasStandards) setRewriteWantStandards(false);
  }, [subjectHasStandards]);

  useEffect(() => {
    if (selectedTptProductForRewrite) {
      const lastContext = selectedTptProductForRewrite.lastRewriteContext;
      setRewriteProductTitle(selectedTptProductForRewrite.productTitle || '');
      setExistingDescriptionInput(selectedTptProductForRewrite.rewrittenDescription || selectedTptProductForRewrite.originalDescription || '');
      setRewrittenDescriptionOutput('');
      setRewrittenNewTitle('');
      setSeoInsights(null);
      setRewritePrimarySubject(selectedTptProductForRewrite.primarySubject || '');
      setRewriteGradeLevels(selectedTptProductForRewrite.gradeLevels || []);
      setRewriteResourceComponents(selectedTptProductForRewrite.resourceComponents || []);
      setRewritePedagogicalApproaches(lastContext?.pedagogicalApproaches || []);
      setRewriteSeasonalTheme(lastContext?.seasonalTheme || '');
      // Handle related products correctly, ensuring it's an array of objects
      const relatedUrls = lastContext?.relatedProducts || [];
      if (Array.isArray(relatedUrls) && relatedUrls.length > 0) {
        setRewriteRelatedProducts(relatedUrls.map((url, idx) => ({ id: Date.now() + idx, url })));
      } else {
        setRewriteRelatedProducts([{ id: Date.now(), url: '' }]);
      }
      setRewriteSuggestNewTitle(false);
      setRewriteSuggestLearningOutcomes(lastContext?.suggestLearningOutcomes ?? true);
      setRewriteIncludeRelatedProducts(lastContext?.includeRelatedProducts ?? true);
      setRewriteSpecifics(lastContext?.specifics || '');
      setRewriteTone(lastContext?.tone || DESCRIPTION_TONES[0]);
      setRewriteDescriptionLength(lastContext?.length || DESCRIPTION_LENGTHS_REWRITE[1].value);
      setRewriteWantStandards(false);
      setRewriteSelectedDomains([]);
      setRewriteSelectedStandardCodes([]);
      setRewriteError('');
      setRefineRequest(''); // NEW: Clear refine request
      setOutputViewMode('preview');
      setInputViewMode('edit');
    }
  }, [selectedTptProductForRewrite]);


  useEffect(() => {
    if (rewriteWantStandards) {
      const availableIds = new Set(filteredStandards.map(s => s.id));
      setRewriteSelectedStandardCodes(prevSelected => prevSelected.filter(id => availableIds.has(id)));
    }
  }, [filteredStandards, rewriteWantStandards]);

  const handleCheckboxChange = (setter, value, currentValues) => {
    const newValues = currentValues.includes(value)
      ? currentValues.filter(item => item !== value)
      : [...currentValues, value];
    setter(newValues);
  };

  const generateRewritePromptString = () => {
    const standardsData = rewriteSelectedStandardCodes
      .map(id => filteredStandards.find(s => s.id === id))
      .filter(Boolean);

    const promptData = `---
**EXISTING PRODUCT DESCRIPTION (HTML TO ANALYZE):**
${existingDescriptionInput}
---
**NEW CONTEXT & INSTRUCTIONS TO INCORPORATE:**
- Product Name: "${rewriteProductTitle || 'This resource'}"
- Grade Level(s): "${rewriteGradeLevels.join(', ')}"
- Primary Subject: "${rewritePrimarySubject}"
- Is the resource editable?: ${isEditable ? '"Yes"' : '"No"'}
- Seasonal Theme: "${rewriteSeasonalTheme}"
- Resource Components: "${rewriteResourceComponents.join(', ')}"
- Key Pedagogical Approaches: "${rewritePedagogicalApproaches.join(', ')}"
- Aligned Standards to Include: "${standardsData.map(s => `${s.code}: ${s.description}`).join('; ')}"
- Other Keywords or Specifics to Focus On: "${rewriteSpecifics}"
---`;
    let promptInstructions = `**YOUR TASK:**
You are an expert TPT copywriter and SEO specialist. Your goal is to completely revitalize the provided "EXISTING PRODUCT DESCRIPTION" by analyzing it, extracting its core information, and rebuilding it into a new, compelling, and highly optimized description. You must incorporate the "NEW CONTEXT & INSTRUCTIONS" provided.
Your rewritten description's tone should be **${rewriteTone}**.

**CRITICAL SEO RULE:**
Prioritize and retain the specific, niche keywords found in the "Product Name" and "Other Keywords or Specifics to Focus On". **Do not over-generalize.** For example, if a product is an "ESL Verb to be worksheet," you must feature that exact phrase. Do not broaden it to "ESL grammar worksheet," as that hurts search visibility. Specificity is key.

**RESOURCE FEATURES:**
If the resource is marked as "Editable: Yes", you should highlight this as a key benefit (e.g., "Fully editable to meet your students' needs!"). If it is marked "Editable: No", you MUST NOT mention editability at all.

If a "Seasonal Theme" is provided, subtly weave it into the tone and examples. For instance, a 'Halloween' theme might use words like 'spooktacular' or 'treat your students' in the opening paragraphs.

The final output must be well-structured HTML. Use <h3> for main headings${rewriteIncludeEmojis ? ' with emojis' : ' (no emojis — plain text headings only)'}, <strong> for emphasis, and <ul> with <li> for lists.
${rewriteIncludeEmojis ? '' : 'IMPORTANT: Do NOT include any emoji characters anywhere in the output — not in headings, paragraphs, or lists.'}

**CRITICAL FORMATTING RULE:**
For all paragraphs (like the Hook, Introduction, and Narrative), you MUST use multiple <p> tags to break up thoughts. Each <p> tag should contain only 2-3 sentences maximum to prevent long "walls of text".

**REWRITE STRATEGY:**
1.  **Analyze and Extract:** Read the "EXISTING PRODUCT DESCRIPTION" to understand the core features, benefits, and content of the resource.
2.  **Incorporate New Context:** Seamlessly blend the information from the "NEW CONTEXT & INSTRUCTIONS" (like new keywords, grade levels, etc.) into the rewritten text.
3.  **Rebuild Using Template:** Discard the old structure and rebuild a brand new description following the high-quality, logical template below.`;
    if (rewriteSuggestNewTitle) {
      promptInstructions += `**TITLE INSTRUCTION:**
On the VERY FIRST line of your response, create a compelling, SEO-optimized "super title".
- **Be a Marketer, Not a List:** Your goal is to attract clicks. The title should not just list the topic; it must highlight a benefit, a solution, or an engaging outcome.
- **Length is Key:** Aim for the SEO sweet spot of 70-85 characters. It **MUST NOT EXCEED 85 characters** (including spaces). This is a critical technical limit.
- **Keywords are Crucial:** Seamlessly weave in the most important keywords from the product details.
- **Formatting:** Use separators like "|" or ":" to improve readability if needed. Do NOT include standard codes.
- After the title, add the exact delimiter: _TITLE_DELIMITER_`;
    }

    promptInstructions += `**TEMPLATE TO FILL (in this logical order):**

<p>[HOOK PARAGRAPH: Write a 1-2 sentence compelling hook that addresses a common problem for a teacher of ${rewriteGradeLevels.join(', ')} ${rewritePrimarySubject}. Make it concise and powerful.]</p>

<p>[INTRODUCTION PARAGRAPH: Write a passionate paragraph that introduces the product, "${rewriteProductTitle}", as the perfect solution. Mention the key benefit for the teacher (e.g., saves time, boosts engagement). Adhere to the specified length: **${rewriteDescriptionLength.split('(')[0].trim()}**.]</p>

<h3>${rewriteIncludeEmojis ? '✨ ' : ''}What's Included:</h3>
<ul>
  <li>[List 1-3 key components from "Resource Components" data, synthesizing from the old description]</li>
  <li>[List 1-3 more key components]</li>
  <li>...</li>
</ul>`;
    if (rewriteSuggestLearningOutcomes) {
      promptInstructions += `<h3>${rewriteIncludeEmojis ? '📚 ' : ''}Learning Objectives</h3>
<ul>
  <li>[Suggest 2-3 key learning outcomes based on the product data, e.g., "Students will be able to..."]</li>
</ul>`;
    }

    if (rewriteWantStandards && standardsData.length > 0) {
      const standardsHTML = standardsData.map(s => `<li><strong>${s.code}:</strong> ${s.description}</li>`).join('');
      promptInstructions += `<h3>${rewriteIncludeEmojis ? '🎯 ' : ''}Standards Alignment</h3>
<ul>
    ${standardsHTML}
</ul>`;
    }

    promptInstructions += `<h3>${rewriteIncludeEmojis ? '💡 ' : ''}How to Use This Resource:</h3>
<ul>
  <li>[Suggest 2-3 specific use cases, e.g., "Morning work," "Math centers," "Homework," "Sub plans"]</li>
</ul>

<h3>${rewriteIncludeEmojis ? '✅ ' : ''}Perfect For...</h3>
<ul>
  <li>[List 2-3 teacher scenarios, e.g., "Intervention groups," "Homeschooling," "Test prep"]</li>
</ul>

<h3>${rewriteIncludeEmojis ? '🧠 ' : ''}The Pedagogy: Why It Works</h3>
<p>[Write a paragraph based on the "Key Pedagogical Approaches" data. If empty, write a general paragraph about helping students succeed.]</p>`;

    const validRewriteRelatedProducts = rewriteRelatedProducts
      .map(rp => ({ ...rp, name: extractProductNameFromUrl(rp.url) || "Related Product" }))
      .filter(rp => rp.url && rp.url.trim());

    if (rewriteIncludeRelatedProducts && validRewriteRelatedProducts.length > 0) {
      const relatedProductsHTML = validRewriteRelatedProducts.map(rp => `<li><a href="${rp.url}">${rp.name}</a></li>`).join('');
      promptInstructions += `<h3>You Might Also Like...</h3>
<ul>
    ${relatedProductsHTML}
</ul>`;
    }

    promptInstructions += `[FINAL CLOSING PARAGRAPH & CALL TO ACTION]
Write a final, enthusiastic 1-2 sentence paragraph, followed by a strong, direct call to action like "Click <strong>Add to Cart</strong> now and make learning unforgettable!"

**FINAL INSTRUCTION:**
The final output must be ONLY the rewritten HTML (and title if requested), starting with the hook paragraph or title. Do not include any of the instructions, analysis, or markdown in your response.`;
    return promptInstructions + promptData;
  };

  // --- NEW: Handle Saving Manual Edits ---
  const handleSaveManualEdits = async () => {
    if (!currentUser) {
      triggerAuthModal("Please log in to save changes.");
      return;
    }
    if (!selectedTptProductForRewrite?.id) {
      setRewriteError("No product selected. Cannot save.");
      return;
    }

    setIsSavingManual(true);
    setRewriteError('');

    try {
      const productDocRef = doc(db, "userProducts", currentUser.uid, "products", selectedTptProductForRewrite.id);

      const updateData = {
        productTitle: rewriteProductTitle,
        // We save the content of the "Input" box as the new "rewrittenDescription"
        // This ensures it loads back into this box next time.
        rewrittenDescription: existingDescriptionInput,
        lastUpdated: new Date()
      };

      await updateDoc(productDocRef, updateData);

      // Notify parent to update sidebar title if needed
      if (onSaveComplete) {
        onSaveComplete(selectedTptProductForRewrite.id, updateData);
      }

      setCopySuccess("Manual edits saved successfully!");
      setTimeout(() => setCopySuccess(''), 3000);
    } catch (err) {
      console.error("Error saving manual edits:", err);
      setRewriteError("Failed to save changes.");
    } finally {
      setIsSavingManual(false);
    }
  };


  // --- NEW: Handle Suggest More Titles from existing title + description ---
  const handleSuggestMoreTitles = async () => {
    if (!currentUser) { triggerAuthModal('Please sign up or log in to use this feature.'); return; }
    if (!rewriteProductTitle.trim() && !existingDescriptionInput.trim()) {
      setRewriteError('Please enter a product title or description first.');
      return;
    }

    setIsSuggestingMoreTitles(true);
    setMoreTitleSuggestions([]);
    setShowMoreTitlesPanel(true);
    setRewriteError('');

    // Strip HTML tags from description for analysis
    const descriptionText = existingDescriptionInput
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 1500);

    const combinedText = `${rewriteProductTitle} ${descriptionText}`;

    // --- Scraped metadata from Firestore (imported via scraper) ---
    const scrapedTags = (selectedTptProductForRewrite?.scrapedTypes || []).join(', ');
    const scrapedSubjects = (selectedTptProductForRewrite?.scrapedSubjects || []).join(', ');
    const scrapedStandards = (selectedTptProductForRewrite?.scrapedStandards || []).join(', ');
    const scrapedGradesRaw = (selectedTptProductForRewrite?.scrapedGrades || []).join(', ');

    const scrapedMetaBlock = [
      scrapedTags      && `TPT Tags (scraped): ${scrapedTags}`,
      scrapedSubjects  && `Subject Tags (scraped): ${scrapedSubjects}`,
      scrapedStandards && `Standards (scraped): ${scrapedStandards}`,
      scrapedGradesRaw && `Grades (scraped): ${scrapedGradesRaw}`,
    ].filter(Boolean).join('\n');

    // --- Grade range detection (from title + description + scraped grades) ---
    const gradeMatches = [...combinedText.matchAll(
      /\b(?:grade\s*)?(\d{1,2})(?:st|nd|rd|th)?\s*(?:grade)?\b/gi
    )];
    const gradeNums = [...new Set(
      gradeMatches
        .map(m => parseInt(m[1], 10))
        .filter(n => n >= 1 && n <= 12)
    )].sort((a, b) => a - b);

    const ordinal = n => n === 1 ? '1st' : n === 2 ? '2nd' : n === 3 ? '3rd' : `${n}th`;

    let gradeInstruction = '';
    if (gradeNums.length >= 2) {
      const lo = gradeNums[0], hi = gradeNums[gradeNums.length - 1];
      gradeInstruction = `GRADE RANGE DETECTED: Grades ${lo} through ${hi}.
- Use "Grades ${lo}-${hi}" OR "${ordinal(lo)}-${ordinal(hi)} Grade" consistently.
- Do NOT list grades separately like "5th 6th 7th Grade" — always use the hyphenated range format.
- ✅ Correct: "Algebraic Substitution Grades 6-7 | Differentiated Worksheets Printable PDF"
- ❌ Wrong:   "Algebraic Substitution 6th 7th Grade Worksheets Printable"`;
    } else if (gradeNums.length === 1) {
      gradeInstruction = `GRADE DETECTED: ${ordinal(gradeNums[0])} Grade. Include this in every title near the front.`;
    } else {
      gradeInstruction = `No specific grade detected — use grade context from the description if available, or omit grade.`;
    }

    // --- Seasonal detection ---
    const SEASONAL_KEYWORDS = [
      { pattern: /valentine/i, label: "Valentine's Day" },
      { pattern: /easter/i, label: "Easter" },
      { pattern: /christmas/i, label: "Christmas" },
      { pattern: /halloween/i, label: "Halloween" },
      { pattern: /thanksgiving/i, label: "Thanksgiving" },
      { pattern: /back.to.school/i, label: "Back to School" },
      { pattern: /end.of.year|end of school/i, label: "End of Year" },
      { pattern: /st\.?\s*patrick|saint patrick/i, label: "St. Patrick's Day" },
      { pattern: /earth day/i, label: "Earth Day" },
      { pattern: /mother.?s day/i, label: "Mother's Day" },
      { pattern: /father.?s day/i, label: "Father's Day" },
      { pattern: /new year/i, label: "New Year" },
      { pattern: /spring/i, label: "Spring" },
      { pattern: /summer/i, label: "Summer" },
      { pattern: /fall|autumn/i, label: "Fall" },
      { pattern: /winter/i, label: "Winter" },
      { pattern: /cinco de mayo/i, label: "Cinco de Mayo" },
      { pattern: /100th day/i, label: "100th Day of School" },
    ];

    const detectedSeasonal = SEASONAL_KEYWORDS
      .filter(s => s.pattern.test(combinedText))
      .map(s => s.label);

    const seasonalInstruction = detectedSeasonal.length > 0
      ? `SEASONAL THEME DETECTED: ${detectedSeasonal.join(', ')}.
- At least 2 of your 5 titles MUST include the seasonal theme (e.g. "Valentine's Day", "Easter").
- Place the seasonal keyword near the END of the title after the core topic, unless it's the primary focus.
- Example: "Algebraic Substitution Grades 6-7 | Valentine's Day Math Worksheets Printable"`
      : `No seasonal theme detected — do NOT add any seasonal reference.`;

    // --- "Full Lesson" detection ---
    const isFullLesson = /full\s+lesson/i.test(combinedText);
    const fullLessonInstruction = isFullLesson
      ? `FULL LESSON DETECTED: The product is explicitly described as a "Full Lesson".
- Every title MUST include "Full Lesson" or "Complete Lesson" (never both).
- Place it after the topic and grade, e.g. "...Grades 6-7 | Full Lesson with Differentiated Resources"
- Do NOT drop this phrase — it is a key search term buyers use.`
      : `No "Full Lesson" detected — do not add it unless it genuinely applies.`;

    const prompt = `You are an expert TPT (Teachers Pay Teachers) SEO copywriter. Every title must read like a teacher's actual search query typed into TPT — NOT a product tagline or ad copy. If a title sounds like marketing, it fails.

Your task: generate exactly 5 diverse, keyword-rich alternative titles for the TPT product below.
Read ALL provided data carefully — the description, tags, and standards all contain specific keywords that are more searchable than generic phrases. Use that specificity.

=== PRODUCT DATA ===
Current Title: "${rewriteProductTitle}"
Description: "${descriptionText}"
${scrapedMetaBlock ? `\n=== SCRAPED TPT METADATA ===\n${scrapedMetaBlock}\n` : ''}
=== GRADE GUIDANCE ===
${gradeInstruction}

=== SEASONAL GUIDANCE ===
${seasonalInstruction}

=== FULL LESSON GUIDANCE ===
${fullLessonInstruction}

=== TITLE RULES ===

RULE 1 — CHARACTER COUNT (most important rule):
Each title MUST be 70–87 characters including spaces. Count every character.
Under 70 = rejected. Over 87 = rejected.
Target: 78–85 characters.

To reach 70+ characters you MUST stack ALL of these into every title:
  [main topic keyword] + [grade/grade range] + [resource type] + [use case OR format OR sub-skill]

Here is a worked example from this product:
  "Algebraic Substitution Lesson Grades 6-7 | Differentiated Worksheets PDF" = 73 chars ✅
  "Substitution into Expressions Grades 6-7 | RAG Differentiated Worksheets" = 73 chars ✅
  "Substitution Lesson Grades 6-7 | Integers Decimals Fractions Printable"   = 71 chars ✅
  "Substitution Grades 6-7 Worksheets"                                        = 35 chars ❌ TOO SHORT

RULE 2 — USE THE SCRAPED TAGS:
The TPT Tags, Subject, and Standards fields above contain high-value search terms.
Mine them for specific keywords to include. For example, if tags include "Differentiated" or "RAG" or "Common Core 6.EE.A.2c" — weave those into titles directly.

RULE 3 — VARY STRUCTURE (one of each across the 5 titles):
  #1: [Topic] + [Grade Range] + [Resource Type] + [Format]
  #2: [Sub-skill from description] + [Grade Range] + [Use Case]
  #3: [Use-case lead] + [Topic] + [Grade] (e.g. "Math Intervention | Substitution...")
  #4: [Long-tail search phrase] a teacher would actually type (use description sub-skills)
  #5: Your single strongest overall title — highest SEO value + closest to 85 chars

RULE 4 — GRADE RANGE FORMAT:
Multiple grades → "Grades 6-7" or "6th-7th Grade". NEVER "6th 7th Grade".

RULE 5 — BANNED WORDS (hard stop):
Engaging, Hands-On, Complete, Mastery, Easy, Fun, Perfect, Powerful, Ultimate,
Amazing, Incredible, Effective, Success, Jumpstart, Unleash, Magic, Awesome,
Boost, Transform, Enhance, Exciting, Proven, Simple, Great, Fantastic.

RULE 6 — FORMAT:
Title Case. Pipes (|), commas, or hyphens allowed. No exclamation marks. No emojis.

OUTPUT — return ONLY valid minified JSON, nothing else:
{"titles":["Title 1","Title 2","Title 3","Title 4","Title 5"]}`;

    const callGeminiCloudFunction = httpsCallable(functionsInstance, 'callGeminiAPI');
    try {
      const result = await callGeminiCloudFunction({ prompt, model: 'gemini-2.5-flash-lite' });
      const rawText = result.data?.generatedText || '{}';
      const firstBrace = rawText.indexOf('{');
      const lastBrace = rawText.lastIndexOf('}');
      const cleaned = firstBrace !== -1 ? rawText.slice(firstBrace, lastBrace + 1) : '{}';
      const parsed = JSON.parse(cleaned);
      // JS-side filter: drop anything clearly too short (model still sometimes ignores instructions)
      const MIN_CHARS = 65;
      const titles = Array.isArray(parsed.titles)
        ? parsed.titles.filter(t => t && t.trim().length >= MIN_CHARS)
        : [];
      setMoreTitleSuggestions(titles);
      if (titles.length === 0) setRewriteError('Titles were too short to use — try rewriting the description first for better context, then try again.');
    } catch (err) {
      console.error('Error generating title suggestions:', err);
      setRewriteError('Could not generate title suggestions. Please try again.');
      setShowMoreTitlesPanel(false);
    } finally {
      setIsSuggestingMoreTitles(false);
    }
  };

  const handleCopyMoreTitle = (e, title) => {
    e.stopPropagation();
    navigator.clipboard.writeText(title).then(() => {
      setCopiedMoreTitle(title);
      setTimeout(() => setCopiedMoreTitle(null), 2000);
    });
  };

  // Saves the chosen title to Firebase (productTitle field) AND updates local state —
  // identical behaviour to the "Save Changes" button above the description box.
  const handleUseMoreTitle = async (e, title) => {
    e.stopPropagation();

    // Always update the local input immediately so the user sees the change
    setRewriteProductTitle(title);

    // If there's no product loaded, just update local state and close
    if (!selectedTptProductForRewrite?.id || !currentUser) {
      setShowMoreTitlesPanel(false);
      setMoreTitleSuggestions([]);
      return;
    }

    setSavingMoreTitle(title);
    try {
      const productDocRef = doc(db, 'userProducts', currentUser.uid, 'products', selectedTptProductForRewrite.id);
      const updateData = {
        productTitle: title,
        rewrittenDescription: existingDescriptionInput,
        lastUpdated: new Date(),
      };
      await updateDoc(productDocRef, updateData);

      if (onSaveComplete) {
        onSaveComplete(selectedTptProductForRewrite.id, updateData);
      }

      setCopySuccess('Title saved successfully!');
      setTimeout(() => setCopySuccess(''), 3000);
      setShowMoreTitlesPanel(false);
      setMoreTitleSuggestions([]);
    } catch (err) {
      console.error('Error saving title from suggestions:', err);
      setRewriteError('Failed to save title. Please try the Save Changes button manually.');
    } finally {
      setSavingMoreTitle(null);
    }
  };

  const handleRewriteExistingDescription = async () => {
    if (!currentUser) { triggerAuthModal("Please sign up for a free trial.", 'signup'); return; }
    if (!existingDescriptionInput.trim()) { setRewriteError("Please paste your existing description."); return; }

    setIsRewriting(true);
    setRewriteError('');
    setRewrittenDescriptionOutput('');
    setRewrittenNewTitle('');
    setRefineRequest(''); // NEW: Clear refine request
    setOutputViewMode('preview');

    const promptToSend = generateRewritePromptString();
    const callGeminiCloudFunction = httpsCallable(functionsInstance, 'callGeminiAPI');

    try {
      const result = await callGeminiCloudFunction({ prompt: promptToSend, model: "gemini-2.5-flash-lite" }); // Use 2.5-flash
      let responseText = result.data?.generatedText.trim() || "";

      if (rewriteSuggestNewTitle && responseText.includes('_TITLE_DELIMITER_')) {
        const parts = responseText.split('_TITLE_DELIMITER_');
        setRewrittenNewTitle(parts[0].trim());
        setRewrittenDescriptionOutput(parts[1].trim());
      } else {
        setRewrittenDescriptionOutput(responseText);
      }
      logEvent(analytics, 'rewrite_description');
    } catch (err) {
      setRewriteError(`An error occurred: ${err.message}`);
    } finally {
      setIsRewriting(false);
    }
  };

  // NEW: Handler for refining the description
  const handleRefineDescription = async () => {
    if (!refineRequest.trim()) {
      setRewriteError("Please enter a refinement request.");
      return;
    }
    if (!rewrittenDescriptionOutput.trim()) {
      setRewriteError("Please generate a description first before refining.");
      return;
    }

    setIsRewriting(true); // Reuse the main spinner
    setRewriteError('');

    try {
      const { systemPrompt, userPrompt } = buildRefinePrompt({
        currentTitle: rewriteProductTitle,
        currentDescription: rewrittenDescriptionOutput, // Refine the *output*
        rephraseRequest: refineRequest,
      });

      const fullPrompt = `${systemPrompt}\n\n${userPrompt}`;
      const callGeminiCloudFunction = httpsCallable(functionsInstance, 'callGeminiAPI');

      const result = await callGeminiCloudFunction({
        prompt: fullPrompt,
        model: "gemini-2.5-flash-lite" // Use 2.5-flash
      });

      const text = result.data?.generatedText;
      if (!text) {
        throw new Error("Received an empty response from the AI.");
      }

      setRewrittenDescriptionOutput(text.trim());
      setRefineRequest(''); // Clear the input
      logEvent(analytics, 'refine_description');

    } catch (e) {
      console.error("Error refining description:", e);
      setRewriteError(`Error refining description: ${e.message || 'An unknown error occurred.'} (Code: ${e.code || 'N/A'})`);
    } finally {
      setIsRewriting(false);
    }
  };

  const handleRegenerateTitle = async () => {
    if (!currentUser) { triggerAuthModal("Please sign up for a free trial.", 'signup'); return; }
    setIsSuggestingTitle(true);

    const titlePrompt = `Based on the following comprehensive product details, generate a single, new, compelling, SEO-optimized "super title".
        **RULES:**
        - **Act as an expert TPT marketer.** Your main goal is to create a compelling "super title" that makes teachers want to click. Highlight benefits and solutions, don't just list the topic.
        - **Length:** Aim for the 70-85 character sweet spot. **CRITICAL:** The final title **MUST NOT EXCEED 85 characters** (including spaces).
        - **Keywords:** Prioritize the most important specific keywords from the product details below.
        - **Formatting:** Use separators like "|" or ":" for readability. Do not include standard codes.

        **PRODUCT DETAILS:**
        - Original Title: "${rewriteProductTitle}"
        - Description Content: "${existingDescriptionInput}"
        - Grade Level(s): "${rewriteGradeLevels.join(', ')}"
        - Subject: "${rewritePrimarySubject}"
        - Resource Components: "${rewriteResourceComponents.join(', ')}"
        - Pedagogical Approaches: "${rewritePedagogicalApproaches.join(', ')}"
        - Seasonal Theme: "${rewriteSeasonalTheme}"
        - Other Keywords: "${rewriteSpecifics}"

        Return ONLY the new title text.`;

    const callGeminiCloudFunction = httpsCallable(functionsInstance, 'callGeminiAPI');
    try {
      const result = await callGeminiCloudFunction({ prompt: titlePrompt, model: "gemini-2.5-flash-lite" }); // Use 2.5-flash
      setRewrittenNewTitle(result.data?.generatedText.trim() || "Could not generate a new title.");
    } catch (err) {
      setRewriteError("Could not generate a new title at this time.");
    } finally {
      setIsSuggestingTitle(false);
    }
  };

  const handleFetchSeoInsights = async () => {
    if (!currentUser) { triggerAuthModal("Please sign up for a free trial.", 'signup'); return; }
    if (!existingDescriptionInput.trim() && !rewriteProductTitle.trim()) {
      setRewriteError("Please load a product or provide a title and description for analysis.");
      return;
    }

    setIsFetchingInsights(true);
    setSeoInsights(null);
    setRewriteError('');

    const insightsPrompt = `        As an expert TPT (Teachers Pay Teachers) SEO strategist and marketing copywriter, perform a deep analysis of the provided product data. Your response MUST be a single, valid, minified JSON object.

        **DATA TO ANALYZE:**
        - Product Title: "${rewriteProductTitle}"
        - Product Description: "${existingDescriptionInput}"
        - Grade Level(s): "${rewriteGradeLevels.join(', ')}"
        - Subject: "${rewritePrimarySubject}"

        **REQUIRED JSON STRUCTURE:**
        {
          "seoAnalysis": {
            "strengths": [
              "A 2-3 sentence technical analysis of what the current title and description do well regarding SEO (e.g., keyword placement in title, use of long-tail phrases in description) and teacher appeal."
            ],
            "opportunities": [
              "A 2-3 sentence technical analysis of the biggest areas for improvement, providing actionable advice (e.g., 'The title could be strengthened by adding a student-focused benefit,' or 'The description should include a dedicated section for use-cases to capture more specific searches.')."
            ]
          },
          "keywordStrategy": {
            "primaryKeywords": [
              {"keyword": "Primary Keyword 1 derived from the provided data", "reason": "Explain why this is a core, high-volume term specifically for this resource."},
              {"keyword": "Primary Keyword 2 derived from the provided data", "reason": "Explain why this is another core term for this resource."}
            ],
            "longTailKeywords": [
              {"keyword": "A specific long-tail phrase from the data, written in natural sentence case", "reason": "Explain how this phrase targets a specific teacher need or search query (e.g., 'differentiated math stations for 3rd grade')."},
              {"keyword": "Another specific long-tail phrase from the data, written in natural sentence case", "reason": "Explain how this phrase captures a different niche or use-case relevant to the product."}
            ]
          },
          "titleSuggestions": [
            "Creative & SEO-Optimized Title Suggestion 1 based on the product data",
            "A Different Title Suggestion 2 focusing on a key benefit",
            "A Third Title Suggestion using a question or hook"
          ],
          "descriptionHooks": [
            "A hook that asks a direct, relatable question to the teacher.",
            "A hook that paints a picture of a transformed, more engaged classroom.",
            "A hook that directly states a powerful benefit and solution not always on prep be creative."
          ],
          "thumbnailText": {
            "powerWords": ["List of 4-6 high-impact words directly relevant to the product components or topic"],
            "benefitPhrases": ["List of 4-5 short, student-focused benefit phrases derived from the product's purpose"]
          }
        }
        `;
    const callGeminiCloudFunction = httpsCallable(functionsInstance, 'callGeminiAPI');
    try {
      const result = await callGeminiCloudFunction({ prompt: insightsPrompt, model: "gemini-2.5-flash-lite" }); // Use 2.5-flash
      const rawText = result.data?.generatedText || '{}';

      const cleanedText = rawText.replace(/```json|```/g, '').trim();
      const parsedResponse = JSON.parse(cleanedText);

      setSeoInsights(parsedResponse);
      logEvent(analytics, 'generate_seo_insights');
    } catch (err) {
      console.error("Error generating or parsing SEO insights:", err);
      setRewriteError("An error occurred while fetching SEO insights. The AI may have returned an invalid format.");
    } finally {
      setIsFetchingInsights(false);
    }
  };

  const handleSaveRewrittenDescription = async () => {
    if (!currentUser) {
      triggerAuthModal("Please log in or sign up to save your rewritten descriptions.");
      return;
    }
    if (!selectedTptProductForRewrite?.id) {
      setRewriteError("No product selected from your list. Cannot save.");
      return;
    }
    setIsSavingRewrite(true);
    setRewriteError('');

    const descriptionToSave = rewrittenDescriptionOutput.trim() ? rewrittenDescriptionOutput : existingDescriptionInput;

    const currentRewriteContext = {
      tone: rewriteTone,
      length: rewriteDescriptionLength,
      specifics: rewriteSpecifics,
      relatedProducts: rewriteRelatedProducts.map(rp => rp.url).filter(Boolean),
      suggestLearningOutcomes: rewriteSuggestLearningOutcomes,
      includeRelatedProducts: rewriteIncludeRelatedProducts,
      pedagogicalApproaches: rewritePedagogicalApproaches,
      suggestNewTitle: rewriteSuggestNewTitle,
      seasonalTheme: rewriteSeasonalTheme
    };

    const productDataToSave = {
      rewrittenDescription: descriptionToSave,
      productTitle: rewriteProductTitle,
      primarySubject: rewritePrimarySubject,
      gradeLevels: rewriteGradeLevels,
      resourceComponents: rewriteResourceComponents,
      lastRewriteContext: currentRewriteContext
    };

    const productDocRef = doc(db, "userProducts", currentUser.uid, "products", selectedTptProductForRewrite.id);

    try {
      await updateDoc(productDocRef, productDataToSave);

      if (onSaveComplete) {
        onSaveComplete(selectedTptProductForRewrite.id, productDataToSave);
      }

      logEvent(analytics, 'save_product_description', { product_id: selectedTptProductForRewrite.id });
      setCopySuccess("Rewritten description and context saved permanently!");
    } catch (saveError) {
      console.error("Error saving rewritten description to Firestore:", saveError);
      setRewriteError(`Failed to save to database: ${saveError.message || 'Unknown error'}`);
    } finally {
      setIsSavingRewrite(false);
      setTimeout(() => setCopySuccess(''), 3000);
    }
  };

  /**
   * UPDATED: copyToClipboard
   * Handles both Raw HTML copy and Visual (Rich Text) copy
   * FIX: Injects <br> tags between paragraphs/lists AND HEADERS (h3) to force TPT to display spacing
   */
  const copyToClipboard = async (textToCopy, type, asHtml = false) => {
    if (!textToCopy) return;

    try {
      // --- TPT SPACING FIX ---
      // TPT strips standard CSS margins from <p> and <h3> tags. We must physically inject
      // a <br> tag between these elements to force a visual gap in their editor.

      let formattedContent = textToCopy
        .replace(/<\/p>\s*<p>/g, '</p><br><p>')      // Space between Paragraphs
        .replace(/<\/ul>\s*<p>/g, '</ul><br><p>')    // Space between List and Paragraph
        .replace(/<\/p>\s*<h3>/g, '</p><br><h3>')    // Space between Paragraph and Header (The Emoji Sections)
        .replace(/<\/ul>\s*<h3>/g, '</ul><br><h3>'); // Space between List and Header (The Emoji Sections)
      // -----------------------

      if (asHtml) {
        // Copy Raw HTML (Source Code) - Uses the fixed content
        await navigator.clipboard.writeText(formattedContent);
      } else {
        // Copy Rendered Text (Visual)
        // We use the fixed content here too, so if they paste into a visual editor
        // that strips margins, the <br> is still there as a backup.
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = formattedContent;
        tempDiv.style.position = 'fixed';
        tempDiv.style.left = '-9999px';
        document.body.appendChild(tempDiv);

        const range = document.createRange();
        range.selectNodeContents(tempDiv);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);

        // This executes the copy command on the selected Rendered DOM nodes
        const successful = document.execCommand('copy');

        document.body.removeChild(tempDiv);
        selection.removeAllRanges();

        if (!successful) throw new Error('Copy command failed');
      }

      if (type === 'rewrite') setCopySuccess(asHtml ? 'Raw HTML copied (TPT Optimized)!' : 'Formatted text copied!');
      logEvent(analytics, 'copy_to_clipboard', { content_type: type, format: asHtml ? 'html' : 'rich_text' });
      setTimeout(() => setCopySuccess(''), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      setCopySuccess('Failed to copy. Please try manually.');
    }
  };

  const handleDownloadHtml = (content) => {
    if (!content) return;
    const filename = `${(rewriteProductTitle || 'rewritten_description').trim().replace(/\s+/g, '_')}.html`;
    const blob = new Blob([content], { type: 'text/html;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
    logEvent(analytics, 'download_html', { filename: filename, content_length: content.length });
  };

  const handleEditOnTpt = (type) => {
    const url = selectedTptProductForRewrite?.tptUrl;
    if (!url) {
      setRewriteError("No TPT Product URL available to edit.");
      setTimeout(() => setRewriteError(''), 3000);
      return;
    }

    const idMatch = url.match(/-(\d+)$/);
    if (!idMatch || !idMatch[1]) {
      setRewriteError("Could not extract product ID from URL.");
      setTimeout(() => setRewriteError(''), 3000);
      return;
    }
    const productId = idMatch[1];

    let editUrl;
    if (type === 'product') {
      editUrl = `https://www.teacherspayteachers.com/itemsDigital/editNext/${productId}`;
    } else if (type === 'bundle') {
      editUrl = `https://www.teacherspayteachers.com/My-Product-Bundles/edit/${productId}`;
    }

    if (editUrl) {
      window.open(editUrl, '_blank');
    }
  };

  const addRewriteRelatedProductField = () => setRewriteRelatedProducts(prev => [...prev, { id: Date.now(), url: '' }]);
  const handleRewriteRelatedProductUrlChange = (id, value) => setRewriteRelatedProducts(prev => prev.map(rp => (rp.id === id ? { ...rp, url: value } : rp)));
  const removeRewriteRelatedProductField = (id) => setRewriteRelatedProducts(prev => prev.filter(rp => rp.id !== id));

  // --- NEW: Logic to handle selection from MultiSelectDropdown for RELATED PRODUCTS ---
  const handleRelatedMultiSelect = useCallback((selectedUrls) => {
    setRewriteRelatedProducts(prev => {
      // 1. Keep MANUAL items (entries NOT in the TPT list)
      const manualItems = prev.filter(item => {
        if (!item.url) return true;
        return !userTptProducts.some(p => p.tptUrl === item.url);
      });

      // 2. Add SELECTED items (from dropdown)
      // Reuse existing IDs if the URL already exists to prevent UI jumps
      const managedItems = selectedUrls.map(url => {
        const existing = prev.find(p => p.url === url);
        return existing ? existing : { id: Date.now() + Math.random(), url };
      });

      // 3. Combine. If we have only 1 empty manual slot and we start adding managed items,
      // we can remove the empty slot so it looks cleaner.
      let finalManuals = manualItems;
      if (managedItems.length > 0 && manualItems.length === 1 && !manualItems[0].url.trim()) {
         finalManuals = [];
      }

      const result = [...finalManuals, ...managedItems];

      // Always ensure at least one slot exists if everything is removed
      if (result.length === 0) {
        return [{ id: Date.now(), url: '' }];
      }

      return result;
    });
  }, [userTptProducts]);

  // Calculate current selection state for the dropdown
  // We filter relatedProducts to find only those that match the user's product list
  const currentSelectedDropdownValues = useMemo(() => {
    if (!userTptProducts || userTptProducts.length === 0) return [];

    // Create a Set for faster lookup
    const tptUrls = new Set(userTptProducts.map(p => p.tptUrl));

    // Return only the URLs currently in the input list that MATCH a TPT product
    return rewriteRelatedProducts
      .map(rp => rp.url)
      .filter(url => tptUrls.has(url));
  }, [rewriteRelatedProducts, userTptProducts]);


  // Optimize options for dropdown to avoid re-rendering issues
  const relatedProductOptions = useMemo(() =>
    userTptProducts.map(p => ({ value: p.tptUrl, label: p.productTitle })),
  [userTptProducts]);

  // -----------------------------------------------------------
  // MEMOIZED OPTIONS (PREVENTS RE-RENDERS ON TYPING)
  // -----------------------------------------------------------
  const subjectOptions = useMemo(() =>
    Object.keys(SUBJECT_CONFIG).sort().map(subject => ({ value: subject, label: subject })),
  []);

  const gradeLevelOptions = useMemo(() => GRADE_LEVELS_CONFIG, []);

  const pedagogicalOptions = useMemo(() =>
    PEDAGOGICAL_APPROACHES.map(comp => ({ value: comp, label: comp })),
  []);

  const resourceComponentOptions = useMemo(() =>
    UNIQUE_SORTED_RESOURCE_COMPONENTS.map(comp => ({ value: comp, label: comp })),
  []);

  const seasonalOptions = useMemo(() =>
    SEASONAL_THEMES.map(theme => ({ value: theme, label: theme })),
  []);

  const toneOptions = useMemo(() =>
    DESCRIPTION_TONES.map(tone => ({ value: tone, label: tone })),
  []);

  const lengthOptions = useMemo(() => DESCRIPTION_LENGTHS_REWRITE, []);

  // -----------------------------------------------------------


  return (
    <div id="rewrite-section-anchor" className="relative bg-white p-6 sm:p-8 rounded-xl shadow-2xl space-y-6 mb-8 border-2 border-sky-500">

      {/* --- BUTTONS CONTAINER --- */}
      <div className="absolute top-6 right-6 flex items-center gap-2">
        <button
            type="button"
            onClick={() => {
                setShowInteractiveModal(true);
                if (analytics) {
                    logEvent(analytics, 'guide_click', {
                        content_type: 'interactive_walkthrough',
                        item_id: 'description_rewriter_interactive'
                    });
                }
            }}
            className="flex items-center gap-1.5 text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold px-2 py-1 rounded-md transition-colors"
        >
            <MousePointerClick size={14} />
            Interactive Walkthrough
        </button>

        <a
            href="https://youtu.be/q2GyjPQVb28?si=k49NQ7By56cckBvY&t=83"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold px-2 py-1 rounded-md transition-colors"
        >
            <Video size={14} />
            Watch Tutorial
        </a>
      </div>
      {/* --- END BUTTONS CONTAINER --- */}

      <SectionTitle icon={RefreshCw}>Improve Your Existing Description</SectionTitle>
      <p className="text-xs text-slate-500 -mt-3 mb-3">
        Load a product from the sidebar, or paste any existing description below to get started.
      </p>
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <label htmlFor="rewriteProductTitle" className="block text-sm font-medium text-slate-700">
            Product Title
          </label>
          <button
            type="button"
            onClick={handleSuggestMoreTitles}
            disabled={isSuggestingMoreTitles}
            className="flex items-center gap-1.5 text-xs bg-sky-50 hover:bg-sky-100 text-sky-700 border border-sky-200 font-semibold px-3 py-1.5 rounded-lg shadow-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            title="Generate alternative SEO-optimized title variations based on your current title and description"
          >
            {isSuggestingMoreTitles ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <Sparkles size={13} />
            )}
            {isSuggestingMoreTitles ? 'Generating...' : 'Suggest More Titles'}
          </button>
        </div>
        <input
          type="text"
          id="rewriteProductTitle"
          value={rewriteProductTitle}
          onChange={(e) => setRewriteProductTitle(e.target.value)}
          placeholder="Product title (auto-filled or enter manually)"
          className="w-full p-3 border border-slate-300 rounded-lg shadow-sm focus:ring-2 focus:ring-sky-500 focus:outline-none"
        />

        {/* --- Suggest More Titles Panel --- */}
        {showMoreTitlesPanel && (
          <div className="mt-2 border border-sky-200 rounded-lg bg-sky-50 overflow-hidden">
            {/* Header */}
            <div className="flex items-start justify-between px-4 py-2.5 border-b border-sky-200 bg-sky-100 gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-xs font-semibold text-sky-800">
                    {isSuggestingMoreTitles ? 'Analysing your title & description…' : 'Title Suggestions'}
                  </p>
                  <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full">
                    Beta
                  </span>
                </div>
                {!isSuggestingMoreTitles && (
                  <p className="text-[11px] text-sky-600 mt-0.5 leading-snug">
                    Generated from your current <strong>Product Title</strong> and <strong>Description</strong> above. Saving a title stores it in the app and updates your product.
                    <span className="block mt-1 text-amber-600 font-medium">
                      💡 Tip: For best results, rewrite your description first — richer descriptions produce stronger title suggestions.
                    </span>
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => { setShowMoreTitlesPanel(false); setMoreTitleSuggestions([]); }}
                className="text-sky-500 hover:text-sky-700 p-0.5 rounded flex-shrink-0 mt-0.5"
                aria-label="Close suggestions"
              >
                <X size={14} />
              </button>
            </div>

            {isSuggestingMoreTitles ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 size={20} className="animate-spin text-sky-400" />
              </div>
            ) : (
              <ul className="divide-y divide-sky-100">
                {moreTitleSuggestions.map((title, i) => {
                  const charCount = title.length;
                  const isGood = charCount >= 70 && charCount <= 87;
                  const isSavingThis = savingMoreTitle === title;
                  return (
                    <li
                      key={i}
                      className="flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-sky-100 transition-colors"
                    >
                      <div className="flex-grow min-w-0">
                        <p className="text-sm text-slate-700 font-medium leading-snug">{title}</p>
                        <span className={`text-[10px] font-mono mt-0.5 inline-block ${isGood ? 'text-emerald-600' : 'text-amber-600'}`}>
                          {charCount} chars
                        </span>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {/* Save to Firebase + overwrite field */}
                        <button
                          type="button"
                          onClick={(e) => handleUseMoreTitle(e, title)}
                          disabled={isSavingThis}
                          className="flex items-center gap-1 text-[11px] font-semibold bg-sky-600 hover:bg-sky-700 disabled:bg-sky-400 text-white px-2.5 py-1.5 rounded-md transition-colors"
                          title="Save this title to your product and overwrite the title field"
                        >
                          {isSavingThis ? (
                            <Loader2 size={11} className="animate-spin" />
                          ) : (
                            <Save size={11} />
                          )}
                          {isSavingThis ? 'Saving…' : 'Save'}
                        </button>
                        {/* Copy button */}
                        <button
                          type="button"
                          onClick={(e) => handleCopyMoreTitle(e, title)}
                          className="flex items-center gap-1 text-[11px] font-semibold bg-white hover:bg-slate-100 text-slate-600 border border-slate-200 px-2.5 py-1.5 rounded-md transition-colors"
                          title="Copy to clipboard"
                        >
                          {copiedMoreTitle === title ? (
                            <CheckCircle size={11} className="text-green-500" />
                          ) : (
                            <Copy size={11} />
                          )}
                          {copiedMoreTitle === title ? 'Copied' : 'Copy'}
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
        {/* --- End Suggest More Titles Panel --- */}
      </div>

      <div>
        <div className="flex justify-between items-center mb-1 flex-wrap gap-2">
          <label htmlFor="existingDescriptionInput" className="block text-sm font-medium text-slate-700">Description to Improve/Rewrite <span className="text-red-500">*</span></label>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <button onClick={() => handleEditOnTpt('product')} disabled={!selectedTptProductForRewrite?.tptUrl} className="flex items-center text-xs bg-slate-200 hover:bg-slate-300 text-slate-700 font-medium py-1.5 px-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed" title="Edit Product on TPT">
                <Edit3 size={14} className="mr-1"/> Edit Product
              </button>
              <button onClick={() => handleEditOnTpt('bundle')} disabled={!selectedTptProductForRewrite?.tptUrl} className="flex items-center text-xs bg-slate-200 hover:bg-slate-300 text-slate-700 font-medium py-1.5 px-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed" title="Edit Bundle on TPT">
                <PackageCheck size={14} className="mr-1"/> Edit Bundle
              </button>

              {/* --- NEW: Save Manual Edits Button --- */}
              <button
                onClick={handleSaveManualEdits}
                disabled={isSavingManual || !selectedTptProductForRewrite?.id}
                className="flex items-center text-xs bg-blue-500 hover:bg-blue-600 text-white font-medium py-1.5 px-3 rounded-lg disabled:bg-slate-400"
                title="Save title & description changes manually"
              >
                {isSavingManual ? (
                  <Loader2 size={14} className="mr-1 animate-spin"/>
                ) : (
                  <Save size={14} className="mr-1"/>
                )}
                Save Changes
              </button>
              {/* ------------------------------------------- */}

              {/* --- UPDATED: Input Section Copy Buttons --- */}
              <button onClick={() => copyToClipboard(existingDescriptionInput, 'input', false)} className="flex items-center text-xs bg-slate-200 hover:bg-slate-300 text-slate-700 font-medium py-1.5 px-3 rounded-lg" title="Copy Formatted Text">
                <Copy size={14} className="mr-1"/> Copy Text
              </button>
              <button onClick={() => copyToClipboard(existingDescriptionInput, 'input', true)} className="flex items-center text-xs bg-slate-200 hover:bg-slate-300 text-slate-700 font-medium py-1.5 px-3 rounded-lg" title="Copy Raw HTML">
                <Code2 size={14} className="mr-1"/> Copy HTML
              </button>
              {/* ------------------------------------------- */}

              <button onClick={() => handleDownloadHtml(existingDescriptionInput)} className="flex items-center text-xs bg-slate-200 hover:bg-slate-300 text-slate-700 font-medium py-1.5 px-3 rounded-lg" title="Download HTML">
                <Download size={14} className="mr-1"/> Download
              </button>
            </div>
            <div className="flex items-center rounded-lg bg-slate-200 p-0.5">
              <button onClick={() => setInputViewMode('edit')} className={`text-xs sm:text-sm font-medium py-1 px-3 rounded-md flex items-center ${inputViewMode === 'edit' ? 'bg-white shadow' : 'bg-transparent'}`}><Edit size={14} className="mr-1 sm:mr-2" />Edit</button>
              <button onClick={() => setInputViewMode('html')} className={`text-xs sm:text-sm font-medium py-1 px-3 rounded-md flex items-center ${inputViewMode === 'html' ? 'bg-white shadow' : 'bg-transparent'}`}><Code2 size={14} className="mr-1 sm:mr-2" />HTML</button>
              <button onClick={() => setInputViewMode('preview')} className={`text-xs sm:text-sm font-medium py-1 px-3 rounded-md flex items-center ${inputViewMode === 'preview' ? 'bg-white shadow' : 'bg-transparent'}`}><Eye size={14} className="mr-1 sm:mr-2" />Preview</button>
            </div>
          </div>
        </div>

        {inputViewMode === 'edit' && (
          <ReactQuill theme="snow" value={existingDescriptionInput} onChange={setExistingDescriptionInput} modules={quillModules} className="bg-white" />
        )}
        {inputViewMode === 'html' && (
          <textarea readOnly value={existingDescriptionInput} className="w-full p-3 border border-slate-300 rounded-lg font-mono text-xs bg-slate-50" rows={10}/>
        )}
        {inputViewMode === 'preview' && (
          <div className="prose max-w-none bg-slate-100 p-4 rounded-md border border-slate-200 min-h-[200px] overflow-y-auto" dangerouslySetInnerHTML={{ __html: existingDescriptionInput || "<p class='text-slate-400 italic'>No description loaded. Switch to Edit or HTML to paste.</p>" }} />
        )}
      </div>

      <div className="pt-4 mt-4 border-t border-slate-200">
        <button
          type="button"
          onClick={handleFetchSeoInsights}
          disabled={isFetchingInsights}
          className="w-full sm:w-auto bg-amber-500 hover:bg-amber-600 disabled:bg-slate-400 text-white font-semibold py-2 px-4 rounded-lg shadow-md flex items-center justify-center transition-colors"
        >
          {isFetchingInsights ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Lightbulb className="h-5 w-5 mr-2" />}
          Get SEO & Marketing Insights
        </button>

        {seoInsights && (
          <div className="mt-4 p-4 bg-slate-50 rounded-lg border space-y-6 animate-fade-in">

            {/* Analysis Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                 <h4 className="font-bold text-green-800 mb-2 flex items-center"><CheckCircle size={16} className="mr-2"/> SEO Strengths</h4>
                 <ul className="list-disc list-inside text-sm text-green-700 space-y-1">
                    {seoInsights.seoAnalysis?.strengths?.map((s, i) => <li key={i}>{s}</li>)}
                 </ul>
              </div>
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                 <h4 className="font-bold text-amber-800 mb-2 flex items-center"><Lightbulb size={16} className="mr-2"/> Opportunities</h4>
                 <ul className="list-disc list-inside text-sm text-amber-700 space-y-1">
                    {seoInsights.seoAnalysis?.opportunities?.map((s, i) => <li key={i}>{s}</li>)}
                 </ul>
              </div>
            </div>

            {/* Keyword Strategy */}
            <div className="bg-white p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-800 mb-3">Target Keywords</h4>
                <div className="space-y-3">
                    <div>
                        <span className="text-xs font-semibold text-sky-600 uppercase tracking-wider">Primary Keywords</span>
                        <div className="flex flex-wrap gap-2 mt-1">
                            {seoInsights.keywordStrategy?.primaryKeywords?.map((kw, i) => (
                                <div key={i} className="bg-sky-50 text-sky-700 px-3 py-1 rounded-full text-sm border border-sky-100" title={kw.reason}>
                                    {kw.keyword}
                                </div>
                            ))}
                        </div>
                    </div>
                    <div>
                        <span className="text-xs font-semibold text-indigo-600 uppercase tracking-wider">Long-Tail Phrases</span>
                         <div className="flex flex-wrap gap-2 mt-1">
                            {seoInsights.keywordStrategy?.longTailKeywords?.map((kw, i) => (
                                <div key={i} className="bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full text-sm border border-indigo-100" title={kw.reason}>
                                    {kw.keyword}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

             {/* Title Suggestions */}
             <div className="bg-white p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-800 mb-2">Title Ideas</h4>
                <ul className="space-y-2">
                    {seoInsights.titleSuggestions?.map((title, i) => (
                        <li key={i} className="flex items-center justify-between bg-slate-50 p-2 rounded border border-slate-100 text-sm">
                            <span className="font-medium text-slate-700">{title}</span>
                            <button onClick={() => {setRewriteProductTitle(title); copyToClipboard(title, 'title');}} className="text-xs text-sky-600 hover:text-sky-800 font-bold px-2">Use This</button>
                        </li>
                    ))}
                </ul>
             </div>

              {/* Hooks */}
             <div className="bg-white p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-800 mb-2">Engagement Hooks</h4>
                 <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
                    {seoInsights.descriptionHooks?.map((hook, i) => <li key={i}>{hook}</li>)}
                </ul>
             </div>

             {/* Thumbnail Ideas */}
             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-lg border border-slate-200">
                    <h4 className="font-bold text-slate-800 mb-2 text-sm">Thumbnail Power Words</h4>
                    <div className="flex flex-wrap gap-1">
                        {seoInsights.thumbnailText?.powerWords?.map((w, i) => <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">{w}</span>)}
                    </div>
                </div>
                 <div className="bg-white p-4 rounded-lg border border-slate-200">
                    <h4 className="font-bold text-slate-800 mb-2 text-sm">Benefit Phrases</h4>
                     <ul className="list-disc list-inside text-xs text-slate-600 space-y-1">
                        {seoInsights.thumbnailText?.benefitPhrases?.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                </div>
             </div>
          </div>
        )}
      </div>

      <SectionTitle icon={PackageCheck}>Context for Rewrite (Optional but Recommended)</SectionTitle>
      <p className="text-xs text-slate-500 -mt-3 mb-3 italic">Provide the AI with as much context as possible for the best results.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="rewritePrimarySubject" className="block text-sm font-medium text-slate-700 mb-1">Primary Subject</label>
          <SearchableDropdown
            options={subjectOptions}
            value={rewritePrimarySubject}
            onChange={setRewritePrimarySubject}
            placeholder="-- Select a Subject --"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Grade Level(s)</label>
          <MultiSelectDropdown
            options={gradeLevelOptions}
            selectedValues={rewriteGradeLevels}
            onChange={setRewriteGradeLevels}
            buttonLabelPrefix="Grades"
          />
        </div>
      </div>

      <div className="space-y-2 mt-4 p-2 bg-sky-50 rounded-md border border-sky-100">
        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            className="form-checkbox"
            checked={rewriteWantStandards}
            onChange={(e) => setRewriteWantStandards(e.target.checked)}
            disabled={!subjectHasStandards}
          />
          <span className={!subjectHasStandards ? 'opacity-50' : ''}>
            Align with specific curriculum standards?
          </span>
        </label>
        <p className="text-xs text-slate-500 pl-6 italic">
          {subjectHasStandards
            ? 'Check this to select curriculum standards and have them detailed in your description.'
            : 'Standards are not available for the selected subject yet.'}
        </p>
      </div>

      {rewriteWantStandards && (
        <div className="p-4 border-l-4 border-sky-200 ml-2 space-y-4">
          {rewriteGradeLevels.length === 0 ? (
            <p className="text-xs text-slate-500 pl-1 italic">
              Select at least one grade level to choose domains.
            </p>
          ) : (
            <DomainCheckboxGroup
              title={`${rewritePrimarySubject} Domain(s)/Topic(s)`}
              options={availableDomains}
              selectedValues={rewriteSelectedDomains}
              onChange={(d) => handleCheckboxChange(setRewriteSelectedDomains, d, rewriteSelectedDomains)}
            />
          )}

          <div className="mt-4 min-h-[10rem]">
            {standardsAreLoading && (
              <div className="flex items-center justify-center p-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600"></div>
                <span className="ml-3 text-slate-600">Loading Standards...</span>
              </div>
            )}
            {standardsError && (
              <div className="p-4 text-red-700 bg-red-100 rounded-lg">
                <p>Could not load standards. Please try another subject.</p>
              </div>
            )}
            {!standardsAreLoading && (
              <StandardsSelectionSection
                standards={filteredStandards}
                selectedIds={rewriteSelectedStandardCodes}
                onSelectionChange={(id) => handleCheckboxChange(setRewriteSelectedStandardCodes, id, rewriteSelectedStandardCodes)}
                primarySubject={rewritePrimarySubject}
                gradeLevels={rewriteGradeLevels}
                selectedDomains={rewriteSelectedDomains}
              />
            )}
          </div>
        </div>
      )}

      <div className="mt-4">
        <label className="block text-sm font-medium text-slate-700 mb-1">Pedagogical Approaches</label>
        <p className="text-xs text-slate-500 mb-2 italic">How is this resource meant to be taught? This helps the AI write the 'Teaching Philosophy' section.</p>
        <MultiSelectDropdown
          options={pedagogicalOptions}
          selectedValues={rewritePedagogicalApproaches}
          onChange={setRewritePedagogicalApproaches}
          buttonLabelPrefix="Approaches"
        />
      </div>
      <div className="mt-4">
        <label className="block text-sm font-medium text-slate-700 mb-1">Resource Component(s)</label>
        <p className="text-xs text-slate-500 mb-2 italic">What tangible items are included? This informs the "What's Included" section.</p>
        <MultiSelectDropdown
          options={resourceComponentOptions}
          selectedValues={rewriteResourceComponents}
          onChange={setRewriteResourceComponents}
          buttonLabelPrefix="Components"
        />
      </div>

      <div className="mt-4">
        <label htmlFor="rewriteSeasonalTheme" className="block text-sm font-medium text-slate-700 mb-1">
          <Gift size={16} className="inline mr-2 text-rose-500"/>
          Seasonal Theme (Optional)
        </label>
        <p className="text-xs text-slate-500 mb-2 italic">Select a theme to tailor the description for a specific time of year.</p>
        <SearchableDropdown
          options={seasonalOptions}
          value={rewriteSeasonalTheme}
          onChange={setRewriteSeasonalTheme}
          placeholder="-- No Specific Theme --"
        />
      </div>

      <div className="mt-4">
        <label htmlFor="rewriteSpecifics" className="block text-sm font-medium text-slate-700 mb-1">Specifics to Include or Focus On:</label>
        <p className="text-xs text-slate-500 mb-2 italic">Add any keywords, selling points, or details the AI should highlight.</p>
        <textarea id="rewriteSpecifics" value={rewriteSpecifics} onChange={(e) => setRewriteSpecifics(e.target.value)} rows="3" placeholder="e.g., Highlight problem-solving, mention differentiation..." className="w-full p-3 border border-slate-300 rounded-lg" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        <div>
          <label htmlFor="rewriteTone" className="block text-sm font-medium text-slate-700 mb-1">Desired Tone</label>
          <SearchableDropdown
            options={toneOptions}
            value={rewriteTone}
            onChange={setRewriteTone}
            placeholder="Select Tone"
          />
        </div>
        <div>
          <label htmlFor="rewriteDescriptionLength" className="block text-sm font-medium text-slate-700 mb-1">Main Description Length</label>
          <SearchableDropdown
            options={lengthOptions}
            value={rewriteDescriptionLength}
            onChange={setRewriteDescriptionLength}
            placeholder="Select Length"
          />
        </div>
      </div>

      <div className="space-y-2 mt-4 p-3 bg-sky-50 rounded-lg border border-sky-100">
        <p className="text-sm font-medium text-slate-700 mb-1">Fine-Tune the Output:</p>
        <label className="flex items-center space-x-2 cursor-pointer">
          <input type="checkbox" className="form-checkbox" checked={rewriteSuggestNewTitle} onChange={(e) => setRewriteSuggestNewTitle(e.target.checked)} />
          <span>Suggest an SEO-optimized title?</span>
        </label>
        <label className="flex items-center space-x-2 cursor-pointer">
          <input type="checkbox" className="form-checkbox" checked={isEditable} onChange={(e) => setIsEditable(e.target.checked)} />
          <span>Is this resource editable?</span>
        </label>
        <label className="flex items-center space-x-2 cursor-pointer"><input type="checkbox" className="form-checkbox" checked={rewriteIncludeEmojis} onChange={(e) => setRewriteIncludeEmojis(e.target.checked)} /><span>Include Emojis in Rewrite?</span></label>
        <label className="flex items-center space-x-2 cursor-pointer"><input type="checkbox" className="form-checkbox" checked={rewriteSuggestLearningOutcomes} onChange={(e) => setRewriteSuggestLearningOutcomes(e.target.checked)} /><span>Suggest Learning Objectives?</span></label>
        <label className="flex items-center space-x-2 cursor-pointer"><input type="checkbox" className="form-checkbox" checked={rewriteIncludeRelatedProducts} onChange={(e) => setRewriteIncludeRelatedProducts(e.target.checked)} /><span>Include Related Products (if provided below)?</span></label>
      </div>

      <SectionTitle icon={Link2}>Related Products/Resources (Optional)</SectionTitle>
      <p className="text-xs text-slate-500 -mt-3 mb-3 italic">Link to other TPT products to encourage cross-selling.</p>

      <div className={`mb-3 ${!currentUser ? 'opacity-50 pointer-events-none' : ''}`}>
        <label className="block text-sm font-medium text-slate-700 mb-1">Select from My TPT Products:</label>
        {/* --- REPLACED <SearchableDropdown> with <MultiSelectDropdown> --- */}
        <MultiSelectDropdown
          options={relatedProductOptions}
          selectedValues={currentSelectedDropdownValues}
          onChange={handleRelatedMultiSelect}
          placeholder="-- Select products from your list --"
          buttonLabelPrefix="Products"
        />
        {/* --------------------------------------------------- */}
      </div>

      <div className="space-y-3">
        {rewriteRelatedProducts.map((rp, index) => (
          <div key={rp.id} className="flex items-center space-x-2">
            <input type="url" value={rp.url} onChange={(e) => handleRewriteRelatedProductUrlChange(rp.id, e.target.value)} placeholder={`Product URL ${index + 1}`} className="w-full p-3 border border-slate-300 rounded-lg" />
            <button type="button" onClick={() => removeRewriteRelatedProductField(rp.id)} className="p-2 text-red-500 hover:text-red-700"><XCircle size={20} /></button>
          </div>
        ))}
        <button type="button" onClick={addRewriteRelatedProductField} className="text-sm text-sky-600 hover:text-sky-800 font-medium flex items-center"><PlusCircle size={18} className="mr-1" /> Add Another URL</button>
      </div>

      <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mt-6">
        <button type="button" onClick={handleRewriteExistingDescription} disabled={isRewriting || !existingDescriptionInput.trim()} className="w-full sm:w-auto bg-green-600 hover:bg-green-700 disabled:bg-slate-400 text-white font-semibold py-3 px-8 rounded-lg shadow-md flex items-center justify-center">
          {isRewriting ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Sparkles className="h-5 w-5 mr-2" />}
          Rewrite My Description
        </button>
      </div>

      {rewriteError && (<div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">{rewriteError}</div>)}
      {copySuccess && (<div className="mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg text-sm flex items-center"><CheckCircle className="h-4 w-4 mr-2" />{copySuccess}</div>)}

      {(rewrittenDescriptionOutput || rewrittenNewTitle) && (
        <div className="mt-6">
          {rewrittenNewTitle && (
            <div className="mb-4 p-4 bg-sky-100 border-l-4 border-sky-500 rounded-r-lg">
              <h4 className="text-md font-bold text-sky-800">Suggested New Title:</h4>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-2">
                <p className="text-sky-900 font-medium">{rewrittenNewTitle}</p>
                <div className='flex items-center gap-2 flex-shrink-0'>
                  <button
                    onClick={() => {
                      setRewriteProductTitle(rewrittenNewTitle);
                      setCopySuccess("Title updated!");
                      setTimeout(() => setCopySuccess(''), 2000);
                    }}
                    className="bg-sky-500 text-white font-semibold py-1 px-3 rounded-lg text-sm hover:bg-sky-600 flex items-center"
                  >
                    <Wand2 size={14} className="mr-2"/>
                    Use This Title
                  </button>
                  <button
                    onClick={handleRegenerateTitle}
                    disabled={isSuggestingTitle}
                    className="bg-slate-500 text-white font-semibold py-1 px-3 rounded-lg text-sm hover:bg-slate-600 flex items-center disabled:bg-slate-300"
                  >
                    {isSuggestingTitle ? <Loader2 size={14} className="mr-2 animate-spin"/> : <RefreshCw size={14} className="mr-2"/>}
                    Regenerate
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-3 gap-2">
            <h3 className="text-lg font-semibold text-sky-700">Suggested Rewrite:</h3>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center rounded-lg bg-slate-200 p-0.5">
                <button onClick={() => setOutputViewMode('preview')} className={`text-xs sm:text-sm font-medium py-1 px-3 rounded-md flex items-center ${outputViewMode === 'preview' ? 'bg-white shadow' : 'bg-transparent'}`}><Eye size={16} className="mr-1 sm:mr-2" />Preview</button>
                <button onClick={() => setOutputViewMode('html')} className={`text-xs sm:text-sm font-medium py-1 px-3 rounded-md flex items-center ${outputViewMode === 'html' ? 'bg-white shadow' : 'bg-transparent'}`}><Code2 size={16} className="mr-1 sm:mr-2" />HTML</button>
                <button onClick={() => setOutputViewMode('edit')} className={`text-xs sm:text-sm font-medium py-1 px-3 rounded-md flex items-center ${outputViewMode === 'edit' ? 'bg-white shadow' : 'bg-transparent'}`}><Edit size={16} className="mr-1 sm:mr-2" />Edit</button>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={handleSaveRewrittenDescription} disabled={isSavingRewrite || !currentUser} className="flex items-center text-xs sm:text-sm bg-blue-500 hover:bg-blue-600 text-white font-medium py-1.5 px-3 rounded-lg"><Save size={16} className="mr-1 sm:mr-2" /> Save</button>

                {/* --- UPDATED: Output/Bottom Section Copy Buttons --- */}
                {/* --- VISUAL COPY BUTTON (Formatted Text) --- */}
                <button onClick={() => copyToClipboard(rewrittenDescriptionOutput, 'rewrite', false)} className="flex items-center text-xs sm:text-sm bg-slate-600 hover:bg-slate-700 text-white font-medium py-1.5 px-3 rounded-lg" > <Copy size={16} className="mr-1 sm:mr-2" /> Copy Text</button>

                {/* --- HTML COPY BUTTON (Source Code) --- */}
                <button onClick={() => copyToClipboard(rewrittenDescriptionOutput, 'rewrite', true)} className="flex items-center text-xs sm:text-sm bg-slate-500 hover:bg-slate-600 text-white font-medium py-1.5 px-3 rounded-lg" title="Copy raw HTML for TPT"> <Code2 size={16} className="mr-1 sm:mr-2" /> Copy HTML</button>
                {/* ------------------------------------------------- */}

                <button onClick={() => handleDownloadHtml(rewrittenDescriptionOutput)} className="flex items-center text-xs sm:text-sm bg-slate-600 hover:bg-slate-700 text-white font-medium py-1.5 px-3 rounded-lg" > <Download size={16} className="mr-1 sm:mr-2" /> Download</button>
              </div>
            </div>
          </div>

          <p className="text-xs text-slate-500 mb-2 italic">
            Click 'Save' to store this rewrite. It will then appear in the input box above the next time you load this product.
          </p>

          {outputViewMode === 'preview' && (
            <div className="prose max-w-none bg-sky-50 p-4 rounded-md border border-sky-200 overflow-y-auto max-h-[500px]" dangerouslySetInnerHTML={{ __html: rewrittenDescriptionOutput }} />
          )}
          {outputViewMode === 'edit' && (
            <ReactQuill theme="snow" value={rewrittenDescriptionOutput} onChange={setRewrittenDescriptionOutput} modules={quillModules} className="bg-white" />
          )}
          {outputViewMode === 'html' && (
            <textarea readOnly value={rewrittenDescriptionOutput} className="w-full p-3 border border-slate-300 rounded-lg font-mono text-xs bg-slate-50" rows={15}/>
          )}

          {/* --- NEW REFINE BOX --- */}
          {rewrittenDescriptionOutput && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg mt-4">
              <div className="p-3 space-y-2">
                <label className="text-sm font-medium text-slate-700 block">Refine Description (Optional)</label>
                <textarea
                  className="w-full p-2 border border-slate-300 rounded-lg shadow-sm focus:ring-2 focus:ring-sky-500 text-sm"
                  rows="2"
                  placeholder="e.g., Make it more exciting, focus on 6th grade, make the hook shorter..."
                  value={refineRequest}
                  onChange={(e) => setRefineRequest(e.target.value)}
                />
                <button
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium shadow-sm"
                  onClick={handleRefineDescription}
                  disabled={isRewriting || !refineRequest}
                >
                  {isRewriting ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />}
                  Refine
                </button>
              </div>
            </div>
          )}
          {/* --- END NEW REFINE BOX --- */}

        </div>
      )}

      {/* --- NEW INTERACTIVE WALKTHROUGH MODAL --- */}
      {showInteractiveModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setShowInteractiveModal(false)}
        >
          {/* Use max-w-7xl for a wider modal */}
          <div
            className="w-full max-w-7xl rounded-xl bg-slate-800 border border-slate-700 shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 border-b border-slate-700 flex-shrink-0">
              <h3 className="text-lg font-semibold text-sky-400">
                Interactive Walkthrough
              </h3>
              <button
                type="button"
                onClick={() => setShowInteractiveModal(false)}
                className="text-slate-300 hover:text-white p-1 rounded-full hover:bg-slate-700"
                aria-label="Close modal"
              >
                <X size={24} />
              </button>
            </div>

            {/* Supademo Embed Code */}
            <div
                style={{
                    position: 'relative',
                    boxSizing: 'content-box',
                    maxHeight: '80vh',
                    width: '100%',
                    aspectRatio: '2.013986013986014',
                    padding: '40px 0 40px 0',
                    background: '#1e293b' // Dark background
                }}
            >
                <iframe
                    src="https://app.supademo.com/embed/cmi5ope742tyzb7b4cwuav7rv?embed_v=2&utm_source=embed"
                    loading="lazy"
                    title="Mr Joel's Seller Tools: Rewrite SEO Optimize TPT Product Descriptions"
                    allow="clipboard-write"
                    frameBorder="0"
                    webkitallowfullscreen="true"
                    mozallowfullscreen="true"
                    allowFullScreen
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%'
                    }}
                ></iframe>
            </div>
          </div>
        </div>
      )}
      {/* --- END INTERACTIVE WALKTHROUGH MODAL --- */}
    </div>
  );
};
export default RewriteDescriptionForm;